import { errorIsStatus } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  Avatar,
  ColumnDefinition,
  Container,
  CopyToClipboard,
  DataTable,
  DialogTrigger,
  HoverInfo,
  Icon,
  MediaQuery,
  Pagination,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import type { Key, Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { BulkMarkMeetsActionBar } from "./BulkMarkMeetsActionBar";
import { CheckTypeFilter } from "./CheckTypeFilter";
import { DateOrderedFilter } from "./DateOrderedFilter";
import { DecisionFilter } from "./DecisionFilter";
import { GroupFilter } from "./GroupFilter";
import { InviteMethodFilter } from "./InviteMethodFilter";
import { NameTableCell } from "./NameTableCell";
import { PackageFilter } from "./PackageFilter";
import { QueryParamBooleanFilter } from "./QueryParamBooleanFilter";
import { RequestorFilter } from "./RequestorFilter";
import { ScoreFilter } from "./ScoreFilter";
import { StatusFilter } from "./StatusFilter";
import { StatusPopover } from "./StatusPopover";
import { TableDataCard } from "./TableDataCard";
import { TableEmptyState } from "./TableEmptyState";
import { TagFilter } from "./TagFilter";
import { CrossTabSearchMessage } from "./CrossTabSearchMessage";
import { FilterBar } from "../../../components/filters/FilterBar";
import {
  useCanViewCaseScore,
  useCanViewCaseDetails,
} from "../../../components/permissions/Permissions";
import { SortByDateFilter } from "../../../components/sort-by-date-filter/SortByDateFilter";
import { TagList } from "../../../components/tag-list/TagList";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useUser } from "../../../hooks/useUser";
import { renderStatusTag } from "../../../utils/renderStatusTag";
import { StatusSubset } from "../types";
import { mapResultsToRows } from "../utils";

interface CasesTableProps {
  cases: components["schemas"]["PaginatedBaseCaseListList"] | undefined;
  isLoading: boolean;
  error: Error | undefined;
  statusSubset: StatusSubset;
}

export function CasesTable({
  cases,
  isLoading,
  error,
  statusSubset,
}: CasesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { params, setParam } = useQueryParams();
  const {
    clientPortalApplyLinksFastFollow,
    clientPortalCaseTags,
    clientPortalSplitUpCaseListEndpoint,
    clientPortalUnifiedCaseSearch,
    enableCentricQuickscreen,
  } = useFlags();
  const isGroupOverview = pathname.includes("/group-overview");
  const canViewScore = useCanViewCaseScore();
  const canViewCaseDetails = useCanViewCaseDetails();
  const { details: userDetails } = useUser();

  const rows = mapResultsToRows(cases?.results);
  const bulkMeetsEligibleIds =
    statusSubset === "review"
      ? rows
          .filter(
            (row) =>
              "can_bulk_mark_meets" in row && row.can_bulk_mark_meets === true
          )
          .map((row) => row.id)
      : [];
  const isBulkSelectable =
    !userDetails?.is_proxy_user && bulkMeetsEligibleIds.length > 0;
  const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [search]);

  function handleSelectionChange(keys: Selection) {
    setSelectedKeys(keys === "all" ? new Set(bulkMeetsEligibleIds) : keys);
  }

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "applicant_details",
      name: t("Name"),
      isRowHeader: true,
      render(row) {
        const {
          applicant_details: applicant,
          linked_cases: linkedCases,
          primary_case: primaryCase,
        } = row;
        const { is_applicant_pay: isApplicantPay } = row;
        const applicantEmail =
          applicant?.email_address ||
          (applicant as { invite_email_address?: string } | undefined)
            ?.invite_email_address;
        return (
          <NameTableCell
            name={applicant?.current_name}
            email={applicantEmail}
            linkedCases={
              primaryCase ? [...linkedCases, primaryCase] : [...linkedCases]
            }
            isApplicantPay={isApplicantPay}
          />
        );
      },
    },
    {
      key: "overall_status",
      name: t("Case status"),
      width: 252,
      render({
        overall_status: status,
        decision,
        overall_score: score,
        popover_details: popoverDetails,
        short_id: id,
        created,
      }) {
        return (
          <DialogTrigger>
            {renderStatusTag({
              status,
              decision,
              score,
              hasPopover: true,
              isScoreHidden: !canViewScore,
            })}
            <StatusPopover
              data={
                !clientPortalSplitUpCaseListEndpoint
                  ? popoverDetails
                  : undefined
              }
              status={
                status as components["schemas"]["ClientPortalOverallStatus"]
              }
              decision={
                decision as components["schemas"]["ClientPortalDecision"]
              }
              caseId={id}
              created={created}
              isCaseNavigationHidden={!canViewCaseDetails}
            />
          </DialogTrigger>
        );
      },
    },
    {
      key: "package",
      name: t("Package"),
      width: 180,
      render({ package: casePackage }) {
        return casePackage?.name || "";
      },
      testID: "package-column-header",
    },
    {
      key: "short_id",
      name: t("Case ID"),
      width: 200,
      render({ short_id: id }) {
        return <CopyToClipboard text={id} testID="copyable-case-id" />;
      },
    },
    {
      key: "created",
      name: t("Order date"),
      allowsSorting: true,
      width: 120,
      render({ created }) {
        return formatDate(created, "PP");
      },
      testID: "order-date-column-header",
    },
    {
      key: "ordered_by",
      name: t("Owner"),
      render({ ordered_by: owner }) {
        return <Avatar name={parseName(owner?.auth0_full_name || undefined)} />;
      },
      width: 82,
    },
    {
      key: "actions",
      isAction: true,
      render({
        short_id: id,
        applicant_details: applicant,
        overall_status: status,
        can_resend_invite: canResendInvite,
      }) {
        const applicantEmail =
          applicant?.email_address ||
          (applicant as { invite_email_address?: string } | undefined)
            ?.invite_email_address ||
          "";

        return (
          <ActionsDropdown
            id={id}
            applicant={[
              applicant?.current_name?.given_name,
              applicant?.current_name?.family_name,
            ]
              .filter((part) => !!part && part.trim().length > 0)
              .join(" ")}
            applicantEmail={applicantEmail}
            status={status}
            canResendInvite={canResendInvite === true}
          />
        );
      },
      width: 48,
    },
  ];

  if (enableCentricQuickscreen) {
    columns.splice(1, 0, {
      key: "is_quickscreen",
      name: "",
      width: 40,
      render({ is_quickscreen }) {
        if (!is_quickscreen) return null;
        return (
          <HoverInfo
            testID="quickscreen-indicator-tooltip"
            trigger={
              <span
                className={styles.quickscreenIcon}
                aria-label={t("Quickscreen")}
              >
                <Icon
                  name="electric_bolt"
                  type="mini"
                  color="text-weak"
                  size="space-4"
                  testID="quickscreen-indicator-icon"
                />
              </span>
            }
            infoContent={
              <Text as="div" style="global/body-secondary">
                {t("Quickscreen")}
              </Text>
            }
          />
        );
      },
    });
  }

  if (clientPortalCaseTags) {
    const statusIndex = columns.findIndex(
      (column) => column.key === "overall_status"
    );
    const packageIndex = columns.findIndex(
      (column) => column.key === "package"
    );
    const insertAt = (packageIndex >= 0 ? packageIndex : statusIndex) + 1;
    columns.splice(insertAt, 0, {
      key: "tag_set",
      name: t("Tags"),
      width: 200,
      render({ tag_set }) {
        const tags = tag_set.flatMap((tag) => tag.name);
        return (
          <TagList
            tags={tags}
            capacity={2}
            hasOverflowDetails
            testID={`${statusSubset}-case`}
          />
        );
      },
    });
  }

  const isSearching = !Object.values(params).every(
    (value) => value === undefined
  );

  return (
    <Container
      layout="full-width"
      testID={`${statusSubset}-cases-tab-container`}
    >
      <FilterBar
        hasSearch
        searchPlaceholder={t("Search by name or email")}
        searchMinLength={3}
        clearFilterExceptions={
          isGroupOverview ? ["customer_group_id"] : undefined
        }
        testID="cases"
      >
        <MediaQuery type="show" sm md>
          <SortByDateFilter />
        </MediaQuery>
        {!isGroupOverview && <GroupFilter />}
        {statusSubset !== "review" && (
          <StatusFilter statusSubset={statusSubset} />
        )}
        {statusSubset === "review" && <ScoreFilter />}
        {statusSubset === "done" && <DecisionFilter />}
        <MediaQuery type="hide" sm md>
          <DateOrderedFilter />
        </MediaQuery>
        <MediaQuery type="hide" sm md>
          <CheckTypeFilter />
        </MediaQuery>
        <RequestorFilter />
        <PackageFilter />
        <TagFilter />
        {clientPortalApplyLinksFastFollow && (
          <MediaQuery type="hide" sm md>
            <InviteMethodFilter />
          </MediaQuery>
        )}
        <QueryParamBooleanFilter
          paramName="is_draft"
          testID="draft-filter"
          label={t("Draft")}
        />
        <QueryParamBooleanFilter
          paramName="is_quickscreen"
          testID="quickscreen-filter"
          label={t("Quickscreen")}
        />
      </FilterBar>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve cases")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <MediaQuery type="hide" sm md>
        <DataTable
          aria-label={t("Cases Table")}
          columns={columns}
          rows={rows}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
          onRowAction={
            canViewCaseDetails
              ? (id) => {
                  const caseData = rows.find((row) => row.short_id === id);
                  const isInDraft = caseData?.overall_status?.name === "DRAFT";

                  if (isInDraft) {
                    navigate(`/quickscreen/${id}`);
                  } else {
                    navigate(`/case-details/${id}`, {
                      state: {
                        last: { href: `${pathname}${search}` },
                      },
                    });
                  }
                }
              : undefined
          }
          renderEmptyState={() => (
            <TableEmptyState
              isSearching={isSearching}
              statusSubset={statusSubset}
            />
          )}
          isLoading={isLoading}
          {...(isBulkSelectable && {
            selectionMode: "multiple",
            selectedKeys,
            onSelectionChange: handleSelectionChange,
            disabledBehavior: "selection",
            disabledKeys: rows
              .map((row) => row.id)
              .filter((id) => !bulkMeetsEligibleIds.includes(id)),
          })}
        />
        {isBulkSelectable && (
          <BulkMarkMeetsActionBar
            selectedCaseIds={Array.from(selectedKeys).map(String)}
            onActionComplete={() => setSelectedKeys(new Set())}
          />
        )}
      </MediaQuery>
      <MediaQuery type="show" sm md>
        {cases?.results?.length ? (
          cases.results.map((item) => {
            return <TableDataCard data={item} key={item.short_id} />;
          })
        ) : (
          <div className={styles.illustration}>
            <TableEmptyState
              isSearching={isSearching}
              statusSubset={statusSubset}
            />
          </div>
        )}
      </MediaQuery>
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={cases?.count ? cases.count : 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
      {clientPortalUnifiedCaseSearch &&
        (statusSubset === "active" ||
          statusSubset === "review" ||
          statusSubset === "done") && (
          <CrossTabSearchMessage
            statusSubset={statusSubset}
            params={params}
            isGroupOverview={isGroupOverview}
          />
        )}
    </Container>
  );
}

const styles = {
  illustration: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }),
  tags: css({
    display: "flex",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  }),
  quickscreenIcon: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "var(--space-6)",
    height: "var(--space-6)",
    borderRadius: "var(--border-radius-sm)",
    backgroundColor: "var(--surface-neutral-subtle)",
  }),
};
